import { Component, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { IPlannedEvent } from "@ramudden/models/planned-event";
import moment from "moment";
import { filter, first, firstValueFrom, of, timeout } from "rxjs";
import { AppService } from "../services/app.service";
import { GlobalEventsService } from "../services/generic/global-events.service";

@Component({
    standalone: true,
    selector: "app-root",
    templateUrl: "./root.component.html",
})
export class RootComponent implements OnInit {
    private readonly appService = inject(AppService);
    private readonly router = inject(Router);
    private readonly globalEventsService = inject(GlobalEventsService);

    async ngOnInit() {
        const plannedEvent = this.appService?.plannedEvent;

        if (this.appService.coordinatorMode && this.appService.config?.isConfigured) {
            // Restored coordinator mode can be stale: whether the account may use it is only known once the
            // authorization info has loaded, which happens after authentication (SMAAS-70). Wait for it, but never
            // longer than a few seconds so a failing authorization cannot leave the user on a blank root page.
            await firstValueFrom(
                this.globalEventsService.authorizationInfo$.pipe(
                    filter((authorizationInfo) => !!authorizationInfo),
                    first(),
                    timeout({ first: 5000, with: () => of(null) }),
                ),
            );

            if (this.appService.coordinatorMode) {
                return this.router.navigate(["team-overview"]);
            }
        }

        if (this.appService.selectedWorker && this.plannedEventStillValid(plannedEvent)) {
            return this.router.navigate(["assignments"]);
        }

        this.appService.clearState();
        return this.router.navigate(["setup"]);
    }

    plannedEventStillValid(plannedEvent?: IPlannedEvent) {
        if (!plannedEvent) return false;

        const now = moment();

        const tomorrow = moment(plannedEvent.eventDate).add(1, "days");
        const shiftEndTime = plannedEvent.isNightShift
            ? moment(tomorrow).set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
            : moment(tomorrow).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

        return now < shiftEndTime;
    }
}
